import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Users, MapPin, Inbox, Briefcase,
  LogOut, Plus, Trash2, Edit2, Loader2, GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import { MasterclassAdmin } from "@/components/admin/MasterclassAdmin";
import { OwnerEnquiriesAdmin } from "@/components/admin/OwnerEnquiriesAdmin";
import { OwnerPhotoUpload } from "@/components/admin/OwnerPhotoUpload";
import { Switch } from "@/components/ui/switch";

const TIERS = ["ignite", "impact", "influence"];

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
        <div className="mt-2 text-3xl font-display font-semibold text-navy">{value}</div>
      </div>
      <div className="w-12 h-12 bg-gold/15 rounded-xl flex items-center justify-center text-navy">
        <Icon size={20} />
      </div>
    </div>
  );
}

function MemberFormDialog({ circles, onSaved, member, trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(
    member || {
      name: "", business_name: "", business_category: "", tier: "ignite",
      city_slug: circles[0]?.slug || "", photo_url: "",
      email: "", phone: "", whatsapp: "", linkedin: "", website: "", bio: "",
    },
  );

  useEffect(() => {
    if (member) setForm(member);
  }, [member]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (member?.id) {
        await api.put(`/admin/members/${member.id}`, form);
        toast.success("Member updated");
      } else {
        await api.post("/admin/members", form);
        toast.success("Member added");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{member ? "Edit Member" : "Add Member"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4" data-testid="member-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input required value={form.name} onChange={update("name")} data-testid="member-input-name" className="mt-1" /></div>
            <div><Label>Business name *</Label><Input required value={form.business_name} onChange={update("business_name")} className="mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Business category</Label><Input value={form.business_category} onChange={update("business_category")} className="mt-1" /></div>
            <div>
              <Label>Tier *</Label>
              <Select value={form.tier} onValueChange={(v) => setForm((f) => ({ ...f, tier: v }))}>
                <SelectTrigger className="mt-1" data-testid="member-select-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>City *</Label>
            <Select value={form.city_slug} onValueChange={(v) => setForm((f) => ({ ...f, city_slug: v }))}>
              <SelectTrigger className="mt-1" data-testid="member-select-city"><SelectValue /></SelectTrigger>
              <SelectContent>
                {circles.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.city}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Photo URL</Label><Input value={form.photo_url} onChange={update("photo_url")} className="mt-1" placeholder="https://..." /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={update("email")} className="mt-1" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={update("phone")} className="mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={update("whatsapp")} className="mt-1" /></div>
            <div><Label>LinkedIn</Label><Input value={form.linkedin} onChange={update("linkedin")} className="mt-1" /></div>
          </div>
          <div><Label>Website</Label><Input value={form.website} onChange={update("website")} className="mt-1" /></div>
          <div><Label>Bio</Label><Textarea value={form.bio} onChange={update("bio")} className="mt-1" rows={3} /></div>
          <button type="submit" disabled={submitting} data-testid="member-form-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white py-3 rounded-full font-semibold hover:bg-navy-deep transition disabled:opacity-60">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {member ? "Update Member" : "Add Member"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const OWNER_FIELDS = [
  ["owner_name", "Owner name"], ["owner_title", "Title"],
  ["owner_phone", "Phone"], ["owner_whatsapp", "WhatsApp"],
  ["owner_email", "Email (receives enquiries)"], ["owner_linkedin", "LinkedIn URL"],
];

function CircleFormDialog({ onSaved, circle, trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(circle || {
    city: "", state: "", is_live: true, description: "",
    meeting_venue: "", meeting_schedule: "",
  });

  useEffect(() => { if (circle) setForm(circle); }, [circle]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (circle?.id) {
        await api.put(`/admin/circles/${circle.id}`, form);
        toast.success("Circle updated");
      } else {
        await api.post("/admin/circles", form);
        toast.success("Circle added");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{circle ? "Edit Circle" : "Add Circle"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4" data-testid="circle-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>City *</Label><Input required value={form.city} onChange={update("city")} className="mt-1" data-testid="circle-input-city" /></div>
            <div><Label>State *</Label><Input required value={form.state} onChange={update("state")} className="mt-1" data-testid="circle-input-state" /></div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={String(form.is_live)} onValueChange={(v) => setForm((f) => ({ ...f, is_live: v === "true" }))}>
              <SelectTrigger className="mt-1" data-testid="circle-input-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Live</SelectItem>
                <SelectItem value="false">Coming Soon</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={update("description")} className="mt-1" rows={3} data-testid="circle-input-description" /></div>
          <div><Label>Meeting Venue</Label><Input value={form.meeting_venue} onChange={update("meeting_venue")} className="mt-1" data-testid="circle-input-venue" /></div>
          <div><Label>Meeting Schedule</Label><Input value={form.meeting_schedule} onChange={update("meeting_schedule")} className="mt-1" placeholder="e.g. Every 2nd & 4th Thursday, 7:30 AM" data-testid="circle-input-schedule" /></div>
          <div className="pt-2 border-t border-gray-100 text-sm font-semibold text-navy">Region owner</div>
          <OwnerPhotoUpload circleId={circle?.id} value={form.owner_photo_url} onUploaded={(url) => setForm((f) => ({ ...f, owner_photo_url: url }))} />
          <div className="grid sm:grid-cols-2 gap-4">
            {OWNER_FIELDS.map(([k, label]) => (
              <div key={k}><Label>{label}</Label><Input value={form[k] || ""} onChange={update(k)} className="mt-1" data-testid={`circle-input-${k}`} /></div>
            ))}
          </div>
          <div><Label>Owner bio</Label><Textarea value={form.owner_bio || ""} onChange={update("owner_bio")} className="mt-1" rows={3} data-testid="circle-input-owner_bio" /></div>
          <div>
            <Label>Owner quote</Label>
            <Textarea value={form.owner_quote || ""} onChange={update("owner_quote")} maxLength={400} className="mt-1" rows={3} data-testid="circle-input-owner_quote" />
            <label className="mt-2 flex items-center gap-3 text-sm">
              <Switch checked={!!form.owner_quote_approved} onCheckedChange={(v) => setForm((f) => ({ ...f, owner_quote_approved: v }))} data-testid="circle-input-owner_quote_approved" />
              Approved, show quote on website
            </label>
          </div>
          <button type="submit" disabled={submitting} data-testid="circle-form-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white py-3 rounded-full font-semibold hover:bg-navy-deep transition disabled:opacity-60">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {circle ? "Update Circle" : "Add Circle"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "visits", label: "Visit Requests", icon: Inbox },
  { id: "franchise", label: "Franchise Apps", icon: Briefcase },
  { id: "masterclass", label: "Masterclass", icon: GraduationCap },
  { id: "enquiries", label: "Owner Enquiries", icon: Inbox },
  { id: "members", label: "Members", icon: Users },
  { id: "circles", label: "Circles", icon: MapPin },
];

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");

  const [visits, setVisits] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [members, setMembers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [mcSessions, setMcSessions] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  const refreshAll = async () => {
    const [v, f, m, c, r, ms, oe] = await Promise.all([
      api.get("/admin/visit-submissions"),
      api.get("/admin/franchise-submissions"),
      api.get("/admin/members"),
      api.get("/admin/circles"),
      api.get("/admin/masterclass-registrations"),
      api.get("/admin/masterclass-sessions"),
      api.get("/admin/owner-enquiries"),
    ]);
    setVisits(v.data); setFranchises(f.data);
    setMembers(m.data); setCircles(c.data);
    setRegistrations(r.data); setMcSessions(ms.data); setEnquiries(oe.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/admin/login");
      return;
    }
    refreshAll().catch(() => toast.error("Failed to load data"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500"><Loader2 className="animate-spin" /></div>;
  }

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/admin/members/${id}`);
    toast.success("Removed");
    refreshAll();
  };

  const deleteCircle = async (id) => {
    if (!window.confirm("Remove this circle?")) return;
    await api.delete(`/admin/circles/${id}`);
    toast.success("Removed");
    refreshAll();
  };

  const circleByslug = (slug) => circles.find((c) => c.slug === slug)?.city || slug;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-white flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="block">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_d32-network-in/artifacts/5qxy1am6_D32BN_logo_India.webp"
            alt="D32 Business Network India"
            className="h-10 w-auto object-contain bg-white rounded px-2 py-1"
          />
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-2">Admin</div>
        </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-nav-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                section === s.id ? "bg-white/10 text-gold" : "text-gray-300 hover:bg-white/[0.04]"
              }`}
            >
              <s.icon size={16} /> {s.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-gray-400 mb-2">{user.email}</div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/[0.05] hover:bg-white/10"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 lg:p-12 overflow-x-hidden">
        {section === "dashboard" && (
          <div data-testid="admin-section-dashboard">
            <h1 className="text-3xl font-display font-semibold text-navy">Welcome back, Admin.</h1>
            <p className="mt-1 text-gray-600">Here's what's coming in across D32 India.</p>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              <StatCard label="Visit Requests" value={visits.length} icon={Inbox} />
              <StatCard label="Franchise Apps" value={franchises.length} icon={Briefcase} />
              <StatCard label="Masterclass" value={registrations.length} icon={GraduationCap} />
              <StatCard label="Active Members" value={members.length} icon={Users} />
              <StatCard label="Circles" value={circles.length} icon={MapPin} />
            </div>

            <div className="mt-10 grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-display text-xl text-navy">Recent Visit Requests</h3>
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                  {visits.slice(0, 6).map((v) => (
                    <div key={v.id} className="flex items-start justify-between border-b border-gray-100 pb-3">
                      <div>
                        <div className="font-medium text-navy">{v.name}</div>
                        <div className="text-xs text-gray-500">{v.business_name || "—"} · {v.city}</div>
                      </div>
                      <div className="text-xs text-gray-400">{new Date(v.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {visits.length === 0 && <div className="text-sm text-gray-500">No requests yet.</div>}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-display text-xl text-navy">Recent Franchise Applications</h3>
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                  {franchises.slice(0, 6).map((f) => (
                    <div key={f.id} className="flex items-start justify-between border-b border-gray-100 pb-3">
                      <div>
                        <div className="font-medium text-navy">{f.name}</div>
                        <div className="text-xs text-gray-500">{f.city_of_interest} · {f.investment_capacity}</div>
                      </div>
                      <div className="text-xs text-gray-400">{new Date(f.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {franchises.length === 0 && <div className="text-sm text-gray-500">No applications yet.</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {section === "visits" && (
          <div data-testid="admin-section-visits">
            <h1 className="text-3xl font-display font-semibold text-navy">Visit Circle Requests</h1>
            <div className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visits.map((v) => (
                    <TableRow key={v.id} data-testid={`visit-row-${v.id}`}>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell>
                        <div className="text-xs">{v.email}</div>
                        <div className="text-xs text-gray-500">{v.phone}</div>
                      </TableCell>
                      <TableCell>{v.city}</TableCell>
                      <TableCell>
                        <div>{v.business_name}</div>
                        <div className="text-xs text-gray-500">{v.business_type}</div>
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate text-sm text-gray-600">{v.message || "—"}</TableCell>
                      <TableCell className="text-xs text-gray-500">{new Date(v.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {visits.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No requests yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {section === "franchise" && (
          <div data-testid="admin-section-franchise">
            <h1 className="text-3xl font-display font-semibold text-navy">Franchise Applications</h1>
            <div className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Occupation</TableHead>
                    <TableHead>Investment</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {franchises.map((f) => (
                    <TableRow key={f.id} data-testid={`franchise-row-${f.id}`}>
                      <TableCell className="font-medium">{f.name}</TableCell>
                      <TableCell>
                        <div className="text-xs">{f.email}</div>
                        <div className="text-xs text-gray-500">{f.phone}</div>
                      </TableCell>
                      <TableCell>{f.city_of_interest}</TableCell>
                      <TableCell className="text-sm">{f.current_occupation || "—"}</TableCell>
                      <TableCell><Badge variant="secondary">{f.investment_capacity}</Badge></TableCell>
                      <TableCell className="text-xs text-gray-500">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {franchises.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No applications yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {section === "enquiries" && <OwnerEnquiriesAdmin enquiries={enquiries} />}

        {section === "masterclass" && (
          <MasterclassAdmin sessions={mcSessions} registrations={registrations} onChange={refreshAll} />
        )}

        {section === "members" && (
          <div data-testid="admin-section-members">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h1 className="text-3xl font-display font-semibold text-navy">Members</h1>
              <MemberFormDialog
                circles={circles}
                onSaved={refreshAll}
                trigger={
                  <button data-testid="add-member-btn" className="inline-flex items-center gap-2 bg-gold text-white font-semibold px-5 py-2.5 rounded-full hover:-translate-y-0.5 transition">
                    <Plus size={16} /> Add Member
                  </button>
                }
              />
            </div>
            <div className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>
                        <div>{m.business_name}</div>
                        <div className="text-xs text-gray-500">{m.business_category}</div>
                      </TableCell>
                      <TableCell>{circleByslug(m.city_slug)}</TableCell>
                      <TableCell>
                        <Badge className="bg-gold/15 text-navy hover:bg-gold/25">
                          {m.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{m.email}</div>
                        <div className="text-gray-500">{m.phone}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <MemberFormDialog
                            circles={circles}
                            member={m}
                            onSaved={refreshAll}
                            trigger={
                              <button data-testid={`edit-member-${m.id}`} className="p-1.5 rounded hover:bg-gray-100"><Edit2 size={14} /></button>
                            }
                          />
                          <button data-testid={`delete-member-${m.id}`} onClick={() => deleteMember(m.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {members.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No members yet. Add the first one.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {section === "circles" && (
          <div data-testid="admin-section-circles">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h1 className="text-3xl font-display font-semibold text-navy">Circles</h1>
              <CircleFormDialog
                onSaved={refreshAll}
                trigger={
                  <button data-testid="add-circle-btn" className="inline-flex items-center gap-2 bg-gold text-white font-semibold px-5 py-2.5 rounded-full hover:-translate-y-0.5 transition">
                    <Plus size={16} /> Add Circle
                  </button>
                }
              />
            </div>
            <div className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>City</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {circles.map((c) => (
                    <TableRow key={c.id} data-testid={`circle-row-${c.id}`}>
                      <TableCell className="font-medium">{c.city}</TableCell>
                      <TableCell>{c.state}</TableCell>
                      <TableCell>
                        <Badge className={c.is_live ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-gray-100 text-gray-600 hover:bg-gray-100"}>
                          {c.is_live ? "Live" : "Coming Soon"}
                        </Badge>
                        {c.owner_quote && !c.owner_quote_approved && (
                          <Badge data-testid={`quote-pending-${c.id}`} className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100">Quote awaiting approval</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{c.meeting_schedule || "—"}</TableCell>
                      <TableCell className="text-sm">{c.meeting_venue || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <CircleFormDialog
                            circle={c}
                            onSaved={refreshAll}
                            trigger={<button data-testid={`edit-circle-${c.id}`} className="p-1.5 rounded hover:bg-gray-100"><Edit2 size={14} /></button>}
                          />
                          <button data-testid={`delete-circle-${c.id}`} onClick={() => deleteCircle(c.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
